/**
 * ImportModal Handlers
 *
 * Pure command handlers for the import publications modal.
 */

import { CommandHandlerMap } from '@commandDrivenStateEngine/core/types';
import { ImportModalState, initialState } from './state';
import { ImportModalCommand } from './commands';

type ImportCommand<T extends string> = Extract<ImportModalCommand, { type: T }>;

// -------- Modal lifecycle handlers ----------

const handleOpen = (
  _state: ImportModalState,
  cmd: ImportCommand<'integrations/channels/importModal/open'>
): ImportModalState => ({
  ...initialState,
  step: 1,
  connectorId: cmd.payload.connectorId,
  integrationUserId: cmd.payload.integrationUserId
});

const handleClose = (
  _state: ImportModalState,
  _cmd: ImportCommand<'integrations/channels/importModal/close'>
): ImportModalState => initialState;

// -------- Mode selection handlers (step 1) ----------

const handleSetMode = (
  state: ImportModalState,
  cmd: ImportCommand<'integrations/channels/importModal/setMode'>
): ImportModalState => ({
  ...state,
  importMode: cmd.payload.mode
});

// -------- Navigation handlers ----------

const handleSetStep = (
  state: ImportModalState,
  cmd: ImportCommand<'integrations/channels/importModal/navigation/setStep'>
): ImportModalState => ({
  ...state,
  step: cmd.payload.step
});

// -------- Preview lifecycle handlers ----------

const handlePreviewSetStatus = (
  state: ImportModalState,
  cmd: ImportCommand<'integrations/channels/importModal/preview/setStatus'>
): ImportModalState => ({
  ...state,
  preview: {
    ...state.preview,
    status: cmd.payload.status,
    errorMessage: cmd.payload.errorMessage ?? null
  }
});

const handlePreviewSetProgress = (
  state: ImportModalState,
  cmd: ImportCommand<'integrations/channels/importModal/preview/setProgress'>
): ImportModalState => ({
  ...state,
  preview: {
    ...state.preview,
    progress: { fetched: cmd.payload.fetched, total: cmd.payload.total }
  }
});

const handlePreviewSetPreviewId = (
  state: ImportModalState,
  cmd: ImportCommand<'integrations/channels/importModal/preview/setPreviewId'>
): ImportModalState => ({
  ...state,
  preview: {
    ...state.preview,
    previewId: cmd.payload.previewId
  }
});

const handlePreviewStop = (
  state: ImportModalState,
  _cmd: ImportCommand<'integrations/channels/importModal/preview/stop'>
): ImportModalState => ({
  ...state,
  preview: {
    ...state.preview,
    status: 'stopped'
  }
});

const handlePreviewDiscard = (
  state: ImportModalState,
  _cmd: ImportCommand<'integrations/channels/importModal/preview/discard'>
): ImportModalState => ({
  ...initialState,
  step: 1,
  connectorId: state.connectorId,
  integrationUserId: state.integrationUserId
});

const handlePreviewResume = (
  state: ImportModalState,
  cmd: ImportCommand<'integrations/channels/importModal/preview/resume'>
): ImportModalState => ({
  ...state,
  step: 2,
  importMode: 'custom',
  preview: {
    ...state.preview,
    previewId: cmd.payload.previewId,
    status: cmd.payload.status as ImportModalState['preview']['status'],
    progress: cmd.payload.progress,
    isResumed: true
  }
});

// -------- Publications data handlers ----------

const handlePublicationsSet = (
  state: ImportModalState,
  cmd: ImportCommand<'integrations/channels/importModal/publications/set'>
): ImportModalState => ({
  ...state,
  publications: cmd.payload.publications,
  pagination: cmd.payload.pagination,
  availableCategories: cmd.payload.categories
});

// -------- Pagination handlers ----------

const handlePaginationSetPage = (
  state: ImportModalState,
  cmd: ImportCommand<'integrations/channels/importModal/pagination/setPage'>
): ImportModalState => ({
  ...state,
  pagination: { ...state.pagination, page: cmd.payload.page }
});

// -------- Filter handlers ----------

const handleFiltersSetImportStatus = (
  state: ImportModalState,
  cmd: ImportCommand<'integrations/channels/importModal/filters/setImportStatus'>
): ImportModalState => ({
  ...state,
  filters: { ...state.filters, importStatus: cmd.payload.importStatus },
  pagination: { ...state.pagination, page: 1 }
});

const handleFiltersSetCategory = (
  state: ImportModalState,
  cmd: ImportCommand<'integrations/channels/importModal/filters/setCategory'>
): ImportModalState => ({
  ...state,
  filters: { ...state.filters, category: cmd.payload.category },
  pagination: { ...state.pagination, page: 1 }
});

// -------- Search handlers ----------

const handleSearchSet = (
  state: ImportModalState,
  cmd: ImportCommand<'integrations/channels/importModal/search/set'>
): ImportModalState => ({
  ...state,
  search: cmd.payload.search,
  pagination: { ...state.pagination, page: 1 }
});

// -------- Selection handlers ----------

const handleSelectionToggle = (
  state: ImportModalState,
  cmd: ImportCommand<'integrations/channels/importModal/selection/toggle'>
): ImportModalState => {
  const { id } = cmd.payload;

  if ( state.selection.mode === 'manual' ) {
    const exists = state.selection.manualSelectedIds.includes( id );
    return {
      ...state,
      selection: {
        ...state.selection,
        manualSelectedIds: exists
          ? state.selection.manualSelectedIds.filter(( sid ) => sid !== id )
          : [ ...state.selection.manualSelectedIds, id ]
      }
    };
  }

  // mode === 'allInFilter': toggle in deselected list (inverted logic)
  const isDeselected = state.selection.manualDeselectedIds.includes( id );
  return {
    ...state,
    selection: {
      ...state.selection,
      manualDeselectedIds: isDeselected
        ? state.selection.manualDeselectedIds.filter(( sid ) => sid !== id )
        : [ ...state.selection.manualDeselectedIds, id ]
    }
  };
};

const handleSelectionToggleAll = (
  state: ImportModalState,
  cmd: ImportCommand<'integrations/channels/importModal/selection/toggleAll'>
): ImportModalState => {
  const { ids } = cmd.payload;

  if ( state.selection.mode === 'manual' ) {
    const allSelected = ids.every(( id ) => state.selection.manualSelectedIds.includes( id ));
    if ( allSelected ) {
      // Remove all page IDs from selection
      const idsSet = new Set( ids );
      return {
        ...state,
        selection: {
          ...state.selection,
          manualSelectedIds: state.selection.manualSelectedIds.filter(( sid ) => !idsSet.has( sid ))
        }
      };
    }
    // Add all missing page IDs to selection
    const existingSet = new Set( state.selection.manualSelectedIds );
    const toAdd = ids.filter(( id ) => !existingSet.has( id ));
    return {
      ...state,
      selection: {
        ...state.selection,
        manualSelectedIds: [ ...state.selection.manualSelectedIds, ...toAdd ]
      }
    };
  }

  // mode === 'allInFilter': toggle page IDs in deselected list
  const allDeselected = ids.every(( id ) => state.selection.manualDeselectedIds.includes( id ));
  if ( allDeselected ) {
    // Remove all page IDs from deselected list (re-select them)
    const idsSet = new Set( ids );
    return {
      ...state,
      selection: {
        ...state.selection,
        manualDeselectedIds: state.selection.manualDeselectedIds.filter(( sid ) => !idsSet.has( sid ))
      }
    };
  }
  // Add all missing page IDs to deselected list
  const existingDeselectedSet = new Set( state.selection.manualDeselectedIds );
  const toDeselect = ids.filter(( id ) => !existingDeselectedSet.has( id ));
  return {
    ...state,
    selection: {
      ...state.selection,
      manualDeselectedIds: [ ...state.selection.manualDeselectedIds, ...toDeselect ]
    }
  };
};

const handleSelectionSelectAllInFilter = (
  state: ImportModalState,
  _cmd: ImportCommand<'integrations/channels/importModal/selection/selectAllInFilter'>
): ImportModalState => ({
  ...state,
  selection: {
    mode: 'allInFilter',
    manualSelectedIds: [],
    manualDeselectedIds: [],
    filterSnapshot: {
      importStatus: state.filters.importStatus,
      category: state.filters.category,
      search: state.search
    }
  }
});

const handleSelectionDeselectAllInFilter = (
  state: ImportModalState,
  _cmd: ImportCommand<'integrations/channels/importModal/selection/deselectAllInFilter'>
): ImportModalState => ({
  ...state,
  selection: {
    mode: 'manual',
    manualSelectedIds: [],
    manualDeselectedIds: [],
    filterSnapshot: null
  }
});

const handleSelectionClear = (
  state: ImportModalState,
  _cmd: ImportCommand<'integrations/channels/importModal/selection/clear'>
): ImportModalState => ({
  ...state,
  selection: {
    mode: 'manual',
    manualSelectedIds: [],
    manualDeselectedIds: [],
    filterSnapshot: null
  }
});

// -------- ID input handlers ----------

const handleIdInputSetText = (
  state: ImportModalState,
  cmd: ImportCommand<'integrations/channels/importModal/idInput/setText'>
): ImportModalState => ({
  ...state,
  idInput: {
    ...state.idInput,
    rawText: cmd.payload.rawText,
    parsedIds: cmd.payload.parsedIds
  }
});

const handleIdInputSetValidation = (
  state: ImportModalState,
  cmd: ImportCommand<'integrations/channels/importModal/idInput/setValidation'>
): ImportModalState => ({
  ...state,
  idInput: {
    ...state.idInput,
    validationResults: cmd.payload.results
  }
});

// -------- Confirm handler ----------

const handleConfirm = (
  state: ImportModalState,
  _cmd: ImportCommand<'integrations/channels/importModal/confirm'>
): ImportModalState => ({
  ...state,
  isLoading: true
});

// -------- Loading handler ----------

const handleLoadingSet = (
  state: ImportModalState,
  cmd: ImportCommand<'integrations/channels/importModal/loading/set'>
): ImportModalState => ({
  ...state,
  isLoading: cmd.payload.isLoading
});

// -------- Handler map ----------

export const handlers = {
  'integrations/channels/importModal/open': handleOpen,
  'integrations/channels/importModal/close': handleClose,
  'integrations/channels/importModal/setMode': handleSetMode,
  'integrations/channels/importModal/navigation/setStep': handleSetStep,
  'integrations/channels/importModal/preview/setStatus': handlePreviewSetStatus,
  'integrations/channels/importModal/preview/setProgress': handlePreviewSetProgress,
  'integrations/channels/importModal/preview/setPreviewId': handlePreviewSetPreviewId,
  'integrations/channels/importModal/preview/stop': handlePreviewStop,
  'integrations/channels/importModal/preview/discard': handlePreviewDiscard,
  'integrations/channels/importModal/preview/resume': handlePreviewResume,
  'integrations/channels/importModal/publications/set': handlePublicationsSet,
  'integrations/channels/importModal/pagination/setPage': handlePaginationSetPage,
  'integrations/channels/importModal/filters/setImportStatus': handleFiltersSetImportStatus,
  'integrations/channels/importModal/filters/setCategory': handleFiltersSetCategory,
  'integrations/channels/importModal/search/set': handleSearchSet,
  'integrations/channels/importModal/selection/toggle': handleSelectionToggle,
  'integrations/channels/importModal/selection/toggleAll': handleSelectionToggleAll,
  'integrations/channels/importModal/selection/selectAllInFilter': handleSelectionSelectAllInFilter,
  'integrations/channels/importModal/selection/deselectAllInFilter': handleSelectionDeselectAllInFilter,
  'integrations/channels/importModal/selection/clear': handleSelectionClear,
  'integrations/channels/importModal/idInput/setText': handleIdInputSetText,
  'integrations/channels/importModal/idInput/setValidation': handleIdInputSetValidation,
  'integrations/channels/importModal/confirm': handleConfirm,
  'integrations/channels/importModal/loading/set': handleLoadingSet
} as CommandHandlerMap<ImportModalState, ImportModalCommand>;
